import { cn } from "@/lib/utils";

interface SealProps {
  title: string;
  subtitle?: string;
  topLabel?: string;
  bottomLabel?: string;
  size?: "sm" | "md" | "lg";
  variant?: "solid" | "outline";
  className?: string;
}

const SIZES = {
  sm: 120,
  md: 180,
  lg: 260,
};

/**
 * Circular medallion: navy fill (or outline), bronze ring,
 * curved top/bottom labels, and Playfair title at center.
 */
export function Seal({
  title,
  subtitle,
  topLabel,
  bottomLabel,
  size = "md",
  variant = "solid",
  className,
}: SealProps) {
  const dim = SIZES[size];
  const isOutline = variant === "outline";

  return (
    <svg
      viewBox="0 0 200 200"
      preserveAspectRatio="xMidYMid meet"
      className={cn("block h-auto w-full", className)}
      style={{ maxWidth: dim }}
      role="img"
      aria-label={title}
    >
      <defs>
        <path id="seal-top" d="M 30,100 A 70,70 0 0 1 170,100" fill="none" />
        <path id="seal-bottom" d="M 30,100 A 70,70 0 0 0 170,100" fill="none" />
      </defs>

      {/* Outer dashed ring */}
      <circle
        cx="100"
        cy="100"
        r="96"
        fill="none"
        stroke="var(--color-bronze-600)"
        strokeWidth="1"
        strokeDasharray="2 3"
        opacity="0.7"
      />
      {/* Bronze ring */}
      <circle
        cx="100"
        cy="100"
        r="88"
        fill={isOutline ? "transparent" : "var(--color-navy-800)"}
        stroke="var(--color-bronze-600)"
        strokeWidth="3"
      />
      {/* Inner hairline */}
      <circle
        cx="100"
        cy="100"
        r="62"
        fill="none"
        stroke={isOutline ? "var(--color-bronze-600)" : "var(--color-cream-50)"}
        strokeWidth="0.8"
        opacity="0.7"
      />

      {/* Curved labels */}
      {topLabel && (
        <text
          fill={isOutline ? "var(--color-navy-800)" : "var(--color-cream-50)"}
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 600,
            fontSize: "10px",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
          }}
        >
          <textPath href="#seal-top" startOffset="50%" textAnchor="middle">
            {topLabel}
          </textPath>
        </text>
      )}
      {bottomLabel && (
        <text
          fill={isOutline ? "var(--color-navy-800)" : "var(--color-cream-50)"}
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 600,
            fontSize: "10px",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
          }}
        >
          <textPath href="#seal-bottom" startOffset="50%" textAnchor="middle">
            {bottomLabel}
          </textPath>
        </text>
      )}

      {/* Center title (Playfair) */}
      <text
        x="100"
        y={subtitle ? 96 : 106}
        textAnchor="middle"
        fill={isOutline ? "var(--color-navy-800)" : "var(--color-cream-50)"}
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: "26px",
        }}
      >
        {title}
      </text>
      {subtitle && (
        <text
          x="100"
          y="118"
          textAnchor="middle"
          fill={isOutline ? "var(--color-charcoal-500)" : "var(--color-bronze-400)"}
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 500,
            fontSize: "9px",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          {subtitle}
        </text>
      )}
    </svg>
  );
}

export default Seal;